import { useState, useCallback, useRef, useEffect } from 'react'
import { recordingService, RecordingState, RecordingOptions } from '../services/recordingService'

export const useRecording = () => {
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    isPaused: false,
    duration: 0,
    error: null
  })

  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const durationIntervalRef = useRef<number | null>(null)

  // Check permission on mount
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const permission = await recordingService.requestMicrophonePermission()
        setHasPermission(permission)
      } catch (error) {
        console.error('Failed to check microphone permission:', error)
        setHasPermission(false)
      }
    }

    checkPermission()
  }, [])

  // Update duration while recording
  useEffect(() => {
    if (recordingState.isRecording && !recordingState.isPaused) {
      durationIntervalRef.current = setInterval(() => {
        const duration = recordingService.getRecordingDuration()
        setRecordingState(prev => ({ ...prev, duration }))
      }, 100)
    } else {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
        durationIntervalRef.current = null
      }
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
      }
    }
  }, [recordingState.isRecording, recordingState.isPaused])

  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      const permission = await recordingService.requestMicrophonePermission()
      setHasPermission(permission)
      return permission
    } catch (error) {
      console.error('Failed to request microphone permission:', error)
      setHasPermission(false)
      setRecordingState(prev => ({
        ...prev,
        error: 'Failed to access microphone. Please check your browser permissions.'
      }))
      return false
    }
  }, [])

  const startRecording = useCallback(async (options?: RecordingOptions): Promise<void> => {
    try {
      setRecordingState(prev => ({
        ...prev,
        isRecording: true,
        isPaused: false,
        error: null,
        duration: 0
      }))

      await recordingService.startRecording(options)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start recording'
      setRecordingState(prev => ({
        ...prev,
        isRecording: false,
        isPaused: false,
        error: errorMessage
      }))
      throw error
    }
  }, [])

  const stopRecording = useCallback((): Blob | null => {
    try {
      const blob = recordingService.stopRecording()
      setRecordingState(prev => ({
        ...prev,
        isRecording: false,
        isPaused: false,
        duration: 0
      }))
      return blob
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to stop recording'
      setRecordingState(prev => ({
        ...prev,
        isRecording: false,
        isPaused: false,
        error: errorMessage
      }))
      return null
    }
  }, [])

  const pauseRecording = useCallback(() => {
    try {
      recordingService.pauseRecording()
      setRecordingState(prev => ({ ...prev, isPaused: true }))
    } catch (error) {
      console.error('Failed to pause recording:', error)
    }
  }, [])

  const resumeRecording = useCallback(() => {
    try {
      recordingService.resumeRecording()
      setRecordingState(prev => ({ ...prev, isPaused: false }))
    } catch (error) {
      console.error('Failed to resume recording:', error)
    }
  }, [])

  const clearError = useCallback(() => {
    setRecordingState(prev => ({ ...prev, error: null }))
  }, [])

  // Format duration for display
  const formatDuration = useCallback((milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recordingService.cleanup()
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
      }
    }
  }, [])

  return {
    recordingState,
    hasPermission,
    requestPermission,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearError,
    formatDuration
  }
}
